import { Item } from "../pojo/Item";
import { Proto } from "../pojo/Proto";
import ServiceStatus = Proto.ServiceStatus;
import { CustomAttributes } from "../pojo/CustomAttributes";
import { S25Util } from "../util/s25-util";
import { ContactService } from "./contact.service";
import { OrganizationMicroService } from "./organization.micro.service";
import { SpaceService } from "./space.service";
import { ResourceMicroService } from "./resource.micro.service";
import { EventMicroService } from "./event.micro.service";

export type CustomAttributeValuePersistOptions = {
    itemTypeId: number;
    itemId: number;
    attributeId: number;
    status: ServiceStatus;
    value?: unknown; // Ignored when status is 'del'
    attributeType?: CustomAttributes.CustAttrCode;
    fileStoreAttributeId?: number;
};

export class CustomAttributeValueUpdateService {
    static normalizeValue(value: unknown): unknown {
        if (value === null || value === undefined) {
            return value;
        }
        const v = value as { imageId?: unknown; itemId?: unknown };
        return v.imageId ?? v.itemId ?? value;
    }

    static usesMicroApi(itemTypeId: number): boolean {
        //Contacts is not using a micro service they are not ready
        return [Item.Ids.Event, Item.Ids.Location, Item.Ids.Resource, Item.Ids.Organization].includes(itemTypeId);
    }

    static async saveAttributeValue(options: CustomAttributeValuePersistOptions): Promise<unknown> {
        const { itemTypeId, itemId, attributeId, status, value } = options;
        const coercedValue = status === "del" ? undefined : CustomAttributeValueUpdateService.normalizeValue(value);

        const result = CustomAttributeValueUpdateService.usesMicroApi(itemTypeId)
            ? await CustomAttributeValueUpdateService.persistMicro(
                  itemTypeId,
                  itemId,
                  attributeId,
                  status,
                  coercedValue,
              )
            : await CustomAttributeValueUpdateService.saveValueWithLegacyService(
                  itemTypeId,
                  itemId,
                  attributeId,
                  status,
                  coercedValue,
              );

        return result;
    }

    /** Contact-only REST payload (`attribute` node). */
    private static buildContactAttributePayload(
        attributeId: number,
        status: ServiceStatus,
        coercedValue: unknown,
    ): Record<string, unknown> {
        return {
            attribute: {
                status,
                attribute_id: attributeId,
                ...(status !== "del" && { attribute_value: coercedValue }),
            },
        };
    }

    /** Legacy Uniface-style API — contacts only. */
    private static async saveValueWithLegacyService(
        itemTypeId: number,
        itemId: number,
        attributeId: number,
        status: ServiceStatus,
        coercedValue: unknown,
    ): Promise<unknown> {
        if (itemTypeId !== Item.Ids.Contact) {
            throw new Error(`Legacy custom attribute update not supported for item type ${itemTypeId}`);
        }
        const payload = CustomAttributeValueUpdateService.buildContactAttributePayload(
            attributeId,
            status,
            coercedValue,
        );
        return ContactService.putContactMinimal(itemId, payload);
    }

    private static buildMicroPayload(
        itemTypeId: number,
        itemId: number,
        attributeId: number,
        status: ServiceStatus,
        coercedValue: unknown,
    ): any {
        let payload: any = {
            attributes: [
                {
                    attributeId,
                    ...(status !== "del" && { value: coercedValue }),
                },
            ],
        };
        if (status === "del") {
            payload = { remove: payload };
        }
        if ([Item.Ids.Location, Item.Ids.Resource].includes(itemTypeId)) {
            payload = { id: itemId, ...payload };
        }
        return payload;
    }

    private static async persistMicro(
        itemTypeId: number,
        itemId: number,
        attributeId: number,
        status: ServiceStatus,
        coercedValue: unknown,
    ): Promise<unknown> {
        const payload = CustomAttributeValueUpdateService.buildMicroPayload(
            itemTypeId,
            itemId,
            attributeId,
            status,
            coercedValue,
        );
        switch (itemTypeId) {
            case Item.Ids.Location:
                return SpaceService.microUpdate(payload);
            case Item.Ids.Resource:
                return ResourceMicroService.saveResource(payload);
            case Item.Ids.Event:
                return EventMicroService.putEventAttributes(payload, itemId);
            case Item.Ids.Organization:
                return OrganizationMicroService.saveOrganization({ id: itemId, ...payload });
            default:
                throw new Error(`Micro custom attribute update not supported for item type ${itemTypeId}`);
        }
    }
}
